import { ScaleLinear, scaleLinear } from 'd3-scale'
import dayjs from 'dayjs'

import {
  Action,
  Pos,
  TimeRange,
  TimelineChangeListener,
  TimeWindowChangeListener
} from './types'

const ONE_HOUR_SEC = 1 * 60 * 60
const ONE_DAY_SEC = 24 * 60 * 60

const TIME_POINT_MIN_WIDTH = 30 // 30px
const DRAG_TIME_X_AXIS_FACTOR = 0.8 // drag mouse 10px, but only recognize it as 8px

const CUR_POS_VERTICAL_LINE_STYLE = 'cornflowerblue'
const CUR_POS_VERTICAL_LINE_WIDTH = 2

const NEW_WINDOW_FILL_STYLE = 'cornflowerblue'
const NEW_WINDOW_ALPHA = 0.3

const WINDOW_FILL_STYLE = '#f0f0f0'
const WINDOW_ALPHA = 0.6
const WINDOW_BORDER_STORKE_STYLE = '#d0d0d0'
const WINDOW_BORDER_ALPHA = 1.0
const WINDOW_BORDER_WIDTH = 1
const WINDOW_RESIZE_LINE_WIDTH = 8
const WINDOW_RESIZE_LINE_WIDTH_HALF = WINDOW_RESIZE_LINE_WIDTH / 2
const WINDOW_RESIZE_STROKE_STYLE = '#ccc'

const MIN_TIME_DELTA = 10 * 60 // 30 min

export class TimeWindowSelectController {
  private context: CanvasRenderingContext2D

  // dimensions
  private width: number = 0
  private height: number = 0
  private timeXAxisHeight: number = 0 // draw x axis for time

  private dragAreaTop: number = 0
  private dragAreaBottom: number = 0

  // time
  private timeOffset: number = 0 // generate time offset by dragging time line

  // scale
  private timeDimenScale: ScaleLinear<number, number> = scaleLinear()

  // mouse pos
  private mouseDownWindowPos: Pos | null = null
  private lastMouseWindowPos: Pos = { x: 0, y: 0 }
  private insideCanvas: boolean = false

  // keyboard
  private ctrlKeyDown: boolean = false // whether ctrl key is pressing down

  // action
  private action = Action.None

  // selected window
  private selectedTimeWindow: TimeRange | null = null
  // time spans
  private timeSpans: TimeRange[] = []

  // listeners
  onTimeWindowChange: TimeWindowChangeListener | undefined
  onTimelineChange: TimelineChangeListener | undefined

  constructor(
    private container: HTMLDivElement,
    private oriStartTime: number, // unix timestamp, use second as unit
    private timeDuration: number = ONE_DAY_SEC
  ) {
    const canvas = document.createElement('canvas')
    this.context = canvas.getContext('2d')!
    container.append(canvas)

    this.setDimensions()
    this.fixPixelRatio()
    this.setTimeDimenScale()

    this.registerHandlers()
    this.draw()
  }

  private setDimensions() {
    this.width = this.container.clientWidth
    this.height = this.container.clientHeight

    this.timeXAxisHeight = Math.floor(this.height / 4)

    const dragAreaHeight = Math.floor(this.height / 5)
    this.dragAreaTop = (this.height - dragAreaHeight) / 2
    this.dragAreaBottom = (this.height + dragAreaHeight) / 2
  }

  private fixPixelRatio() {
    // https://developer.mozilla.org/zh-CN/docs/Web/API/Window/devicePixelRatio
    const dpr = window.devicePixelRatio || 1

    this.context.canvas.style.width = this.width + 'px'
    this.context.canvas.style.height = this.height + 'px'
    this.context.canvas.width = this.width * dpr
    this.context.canvas.height = this.height * dpr
    this.context.scale(dpr, dpr)
  }

  // this method should be called when oriStartTime/timeOffset/timeDuration/width change
  private setTimeDimenScale() {
    this.timeDimenScale = scaleLinear()
      .domain([this.finalStartTime(), this.finalEndTime()])
      .range([0, this.width])
  }

  ////////////////////////////////////////////

  // update time offset when:
  // - drag time line
  // - zoom in/out time line
  private updateTimeOffset(offset: number) {
    this.timeOffset = Math.floor(offset)
    this.setTimeDimenScale()

    this.notifyTimelineListener({
      start: this.finalStartTime(),
      end: this.finalEndTime()
    })
  }

  // update time duration when:
  // - zoom in/out time line
  private updateTimeDuration(timeDuration: number) {
    this.timeDuration = Math.floor(timeDuration)
    this.setTimeDimenScale()

    this.notifyTimelineListener({
      start: this.finalStartTime(),
      end: this.finalEndTime()
    })
  }

  // update selected time window when:
  // - selecting new window
  // - move whole cur window
  // - move cur window left
  // - move cur window right
  // - zoom in/out cur window
  private updateSelectedTimeWindow(newTimeWindow: TimeRange) {
    // check
    const { start, end } = newTimeWindow
    if (end <= start) {
      throw new Error('end must be greater than start')
    }
    const trim = { start: Math.floor(start), end: Math.ceil(end) }
    this.selectedTimeWindow = trim
    this.notifyTimeRangeListener(trim)
  }

  ////////////////////////////////////////////
  // public methods, call by user

  // set selected time window from outside
  // doesn't need to notify listeners
  setSelectedTimeWindow(newTimeWindow: TimeRange | null) {
    if (newTimeWindow === null && this.selectedTimeWindow === null) {
      return
    }
    if (newTimeWindow && this.selectedTimeWindow) {
      if (
        newTimeWindow.start === this.selectedTimeWindow.start &&
        newTimeWindow.end === this.selectedTimeWindow.end
      ) {
        return
      }
    }
    this.selectedTimeWindow = newTimeWindow
    this.draw()
  }

  setTimeSpans(spans: TimeRange[]) {
    this.timeSpans = spans

    if (spans.length > 0 && !this.selectedTimeWindow) {
      for (let i = 0; i < spans.length; i++) {
        let { start, end } = spans[i]
        if (end < this.finalStartTime()) {
          continue
        }
        if (start > this.finalEndTime()) {
          break
        }
        if (end - start < MIN_TIME_DELTA) {
          end = start + MIN_TIME_DELTA
        }
        this.updateSelectedTimeWindow({ start, end })
        break
      }
    }

    this.draw()
  }

  resetStartTime(newStartTime: number) {
    if (Math.floor(newStartTime) === this.finalStartTime()) {
      return
    }

    this.oriStartTime = Math.floor(newStartTime)
    this.timeOffset = 0
    this.setTimeDimenScale()
    this.draw()
  }

  // remember to remove event listeners when unmounting
  // else listeners are still working after unmounting
  cleanup() {
    this.context.canvas.removeEventListener('wheel', this.onMouseWheel)
    this.context.canvas.removeEventListener(
      'contextmenu',
      this.onCanvasContextMenu
    )
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('mouseup', this.onWindowMouseUp)
    window.removeEventListener('mousemove', this.onWindowMouseMove)

    this.context.canvas.removeEventListener('mouseout', this.onCanvasMouseOut)
    this.context.canvas.removeEventListener('mousemove', this.onCanvasMouseMove)
    this.context.canvas.removeEventListener('mousedown', this.onCanvasMouseDown)

    window.removeEventListener('resize', this.onResize)
  }

  /////////////////////////////////////
  // event handlers: resize, mousedown, mousemove, mouseout, mouseup, mousewheel
  private registerHandlers() {
    window.addEventListener('resize', this.onResize)

    this.context.canvas.addEventListener('mousedown', this.onCanvasMouseDown)
    this.context.canvas.addEventListener('mousemove', this.onCanvasMouseMove)
    this.context.canvas.addEventListener('mouseout', this.onCanvasMouseOut)
    window.addEventListener('mousemove', this.onWindowMouseMove)
    window.addEventListener('mouseup', this.onWindowMouseUp)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)

    // to prevent from showing context menu when ctrl+click in the canvas
    this.context.canvas.addEventListener(
      'contextmenu',
      this.onCanvasContextMenu
    )

    // https://developer.mozilla.org/en-US/docs/Web/API/Element/wheel_event
    this.context.canvas.addEventListener('wheel', this.onMouseWheel)
  }

  private onResize = () => {
    this.setDimensions()
    this.fixPixelRatio()
    this.setTimeDimenScale()
    this.draw()
  }

  private onCanvasMouseDown = (event: any) => {
    event.preventDefault() // prevent element selection

    this.mouseDownWindowPos = { x: event.clientX, y: event.clientY }
    this.lastMouseWindowPos = { ...this.mouseDownWindowPos }
  }

  // only handle the mouse move event inside the canvas before pressing the mouse
  // to decide the action
  private onCanvasMouseMove = (event: any) => {
    event.preventDefault()

    this.insideCanvas = true
    // when mouse is down, handle this event in the onWindowMouseMove
    if (this.mouseDownWindowPos) return

    // draw a vertical blue line to indicate current mouse pos
    this.lastMouseWindowPos = { x: event.clientX, y: event.clientY }
    this.updateAction()
    this.draw()
  }

  private onCanvasMouseOut = (event: any) => {
    event.preventDefault()

    this.insideCanvas = false
    // reset mouse cursor to initial when moving mouse out of canvas and mouse is not pressing down
    this.lastMouseWindowPos = { x: event.clientX, y: event.clientY }
    this.updateAction()
    this.draw()
  }

  private onWindowMouseMove = (event: any) => {
    // only response when mouse is down, improve performance
    if (this.mouseDownWindowPos === null) return

    event.preventDefault()

    const deltaX = event.clientX - this.lastMouseWindowPos.x
    this.lastMouseWindowPos = { x: event.clientX, y: event.clientY }
    this.handleMouseMoveWithDown(deltaX)
    this.draw()
  }

  private onWindowMouseUp = (event: any) => {
    event.preventDefault()

    // update selected time window
    if (this.action === Action.SelectingNewWindow) {
      this.handleSelectingNewWindow()
    }

    // release mouse
    this.mouseDownWindowPos = null
    this.updateAction()
    this.draw()
  }

  private onKeyDown = (event: any) => {
    // it seems we should not to prevent default
    // it would stop the page to respond to other keys (for example, cmd+r to reload page)
    // event.preventDefault()

    this.ctrlKeyDown = event.ctrlKey
    this.updateAction()
    this.draw()
  }

  private onKeyUp = (event: any) => {
    // event.preventDefault()

    this.ctrlKeyDown = event.ctrlKey
    this.updateAction()
    this.draw()
  }

  private onCanvasContextMenu = (event: any) => {
    // to prevent from showing context menu when ctrl+click in the canvas
    event.preventDefault()
  }

  private onMouseWheel = (event: any) => {
    // to prevent from scrolling the page
    event.preventDefault()

    if (this.mouseDownWindowPos) {
      return
    }

    const ev = event as WheelEvent
    if (this.action === Action.ModifyTimeline) {
      this.handleZoomTimeline(ev)
    } else {
      this.handleZoomSelectedWindow(ev)
    }
    this.draw()
  }

  //////////////////////////////////////////

  private handleZoomTimeline = (ev: WheelEvent) => {
    const timeDurationDelta = Math.floor(this.timeDuration / 20)

    if (ev.deltaY > 0) {
      // zoom out, reduce the timeDuration, min 1 day
      if (this.timeDuration <= ONE_DAY_SEC) {
        return
      }

      this.updateTimeDuration(this.timeDuration - timeDurationDelta * 2)
      this.updateTimeOffset(this.timeOffset + timeDurationDelta)
      return
    }

    // zoom in, increase the timeDuration, max 3 days
    if (this.timeDuration >= ONE_DAY_SEC * 3) {
      return
    }
    this.updateTimeDuration(this.timeDuration + timeDurationDelta * 2)
    this.updateTimeOffset(this.timeOffset - timeDurationDelta)
  }

  private handleZoomSelectedWindow = (ev: WheelEvent) => {
    if (!this.selectedTimeWindow) {
      return
    }

    const { start, end } = this.selectedTimeWindow
    // defalut zoom out
    let byDelta = Math.floor((end - start) / 20)
    if (ev.deltaY < 0) {
      // zoom in
      byDelta = -byDelta
    }

    let newStart = start - byDelta
    if (newStart < this.finalStartTime()) {
      newStart = this.finalStartTime()
    }
    let newEnd = end + byDelta
    if (newEnd > this.finalEndTime()) {
      newEnd = this.finalEndTime()
    }
    if (newEnd - newStart < MIN_TIME_DELTA) {
      return
    }
    this.updateSelectedTimeWindow({ start: newStart, end: newEnd })
  }

  private handleMouseMoveWithDown = (deltaX: number) => {
    const deltaTime =
      this.timeDimenScale.invert(deltaX) - this.timeDimenScale.invert(0)

    if (this.action === Action.ModifyTimeline) {
      this.updateTimeOffset(
        this.timeOffset - deltaTime * DRAG_TIME_X_AXIS_FACTOR
      )
      return
    }

    // move cur window
    if (!this.selectedTimeWindow) {
      return
    }
    const { start, end } = this.selectedTimeWindow

    if (this.action === Action.MoveCurWindowLeft) {
      const newStart = start + deltaTime
      if (end - newStart < MIN_TIME_DELTA) {
        return
      }
      if (newStart < this.finalStartTime()) {
        return
      }
      this.updateSelectedTimeWindow({ start: newStart, end })
      return
    }
    if (this.action === Action.MoveCurWindowRight) {
      const newEnd = end + deltaTime
      if (newEnd - start < MIN_TIME_DELTA) {
        return
      }
      if (newEnd > this.finalEndTime()) {
        return
      }
      this.updateSelectedTimeWindow({ start, end: newEnd })
      return
    }
    if (this.action === Action.MoveCurWindow) {
      this.updateSelectedTimeWindow({
        start: start + deltaTime,
        end: end + deltaTime
      })
      return
    }
  }

  private handleSelectingNewWindow() {
    const mouseDownCanvasPos = this.windowPosToCanvasPos(
      this.mouseDownWindowPos!
    )
    const mouseDownTime = this.timeDimenScale.invert(mouseDownCanvasPos.x)

    const lastMouseCanvasPos = this.windowPosToCanvasPos(
      this.lastMouseWindowPos
    )
    const lastMouseTime = this.timeDimenScale.invert(lastMouseCanvasPos.x)

    let start, end
    if (lastMouseTime >= mouseDownTime) {
      start = mouseDownTime
      end = lastMouseTime
      if (end - start < MIN_TIME_DELTA) {
        end = start + MIN_TIME_DELTA
      }
    } else {
      start = lastMouseTime
      end = mouseDownTime
      if (end - start < MIN_TIME_DELTA) {
        start = end - MIN_TIME_DELTA
      }
    }

    this.updateSelectedTimeWindow({ start, end })
  }

  //////////////////////////////////////////

  // change the action when moving mouse before pressing the mouse
  private updateAction() {
    if (this.mouseDownWindowPos) {
      return
    }

    this.action = Action.SelectingNewWindow
    const lastMouseCanvasPos = this.windowPosToCanvasPos(
      this.lastMouseWindowPos
    )

    if (!this.insideCanvas) {
      this.action = Action.None
    } else if (this.ctrlKeyDown) {
      this.action = Action.ModifyTimeline
    } else if (
      this.selectedTimeWindow &&
      lastMouseCanvasPos.y >= this.dragAreaTop &&
      lastMouseCanvasPos.y <= this.dragAreaBottom
    ) {
      const { start, end } = this.selectedTimeWindow
      const left = this.timeDimenScale(start)
      const right = this.timeDimenScale(end)

      if (
        lastMouseCanvasPos.x >= left + WINDOW_RESIZE_LINE_WIDTH_HALF &&
        lastMouseCanvasPos.x <= right - WINDOW_RESIZE_LINE_WIDTH_HALF
      ) {
        this.action = Action.MoveCurWindow
      } else if (
        lastMouseCanvasPos.x > left - WINDOW_RESIZE_LINE_WIDTH_HALF &&
        lastMouseCanvasPos.x < left + WINDOW_RESIZE_LINE_WIDTH_HALF
      ) {
        this.action = Action.MoveCurWindowLeft
      } else if (
        lastMouseCanvasPos.x > right - WINDOW_RESIZE_LINE_WIDTH_HALF &&
        lastMouseCanvasPos.x < right + WINDOW_RESIZE_LINE_WIDTH_HALF
      ) {
        this.action = Action.MoveCurWindowRight
      }
    }

    this.updateMouseCursor()
  }

  private updateMouseCursor() {
    // https://developer.mozilla.org/zh-CN/docs/Web/CSS/cursor
    let cursor: string
    switch (this.action) {
      case Action.SelectingNewWindow:
        cursor = 'text'
        break
      case Action.MoveCurWindowLeft:
      case Action.MoveCurWindowRight:
        cursor = 'ew-resize'
        break
      case Action.MoveCurWindow:
      case Action.ModifyTimeline:
        cursor = 'grab'
        break
      default:
        cursor = 'initial'
        break
    }
    document.body.style.cursor = cursor
  }

  //////////////////////////////////////////
  // draw
  private draw() {
    this.context.clearRect(0, 0, this.width, this.height)
    this.drawTimeSpans() // need to draw at first
    this.drawTimeIndicators()
    this.drawCurPosIndicator()
    this.drawMouseDownPos()
    this.drawSelectingNewWindow()
    this.drawSelectedWindow()
  }

  private drawTimeSpans() {
    this.context.save()

    this.context.fillStyle = '#fff2e2'

    this.timeSpans.forEach(({ start, end }) => {
      const x1 = this.timeDimenScale(start)
      const x2 = this.timeDimenScale(end)
      this.context.fillRect(x1, 0, x2 - x1, this.height)
    })

    this.context.restore()
  }

  private drawTimeIndicators() {
    this.context.save()

    // set paint style
    this.context.strokeStyle = '#ccc'
    this.context.lineWidth = 0.5
    // text
    this.context.textAlign = 'center'
    this.context.textBaseline = 'top'

    // draw time x axis
    this.context.beginPath()
    this.context.moveTo(0, this.height - this.timeXAxisHeight)
    this.context.lineTo(this.width, this.height - this.timeXAxisHeight)
    this.context.stroke()

    // draw time points
    const startTimePoint = this.trimStartTime()
    const timeDelta = this.calcXAxisTimeDelta()
    for (let i = 0; i <= 1000; i++) {
      const curTimePoint = startTimePoint + timeDelta * i
      const x = this.timeDimenScale(curTimePoint)
      if (x < 0) {
        continue
      }
      if (x >= this.width) {
        break
      }

      const timeStr = dayjs.unix(curTimePoint).format('H:mm')
      // draw text
      this.context.fillText(timeStr, x, this.height - this.timeXAxisHeight + 4)
      // draw vertical line
      if (timeStr !== '0:00') {
        this.context.beginPath()
        this.context.moveTo(x + 0.5, this.height - this.timeXAxisHeight * 2)
        this.context.lineTo(x + 0.5, this.height - this.timeXAxisHeight)
        this.context.stroke()
      }
    }
    // draw day
    this.context.textAlign = 'left'
    this.context.textBaseline = 'bottom'
    for (let i = 0; i < 1000; i++) {
      const curTimePoint = startTimePoint + ONE_HOUR_SEC * i

      const x = this.timeDimenScale(curTimePoint)
      if (x < 0) {
        continue
      }
      if (x >= this.width) {
        break
      }

      const date = dayjs.unix(curTimePoint)
      const hour = date.hour()
      if (hour !== 0) {
        continue
      }

      const dayStr = date.format('MM-DD')
      // draw text
      this.context.fillText(dayStr, x + 4, this.timeXAxisHeight)
      // draw vertical line
      this.context.beginPath()
      this.context.moveTo(x + 0.5, 0)
      this.context.lineTo(x + 0.5, this.height - this.timeXAxisHeight)
      this.context.stroke()
    }

    this.context.restore()
  }

  private drawMouseDownPos() {
    if (
      this.mouseDownWindowPos === null ||
      this.action !== Action.SelectingNewWindow
    ) {
      return
    }

    const mouseDownCanvasPos = this.windowPosToCanvasPos(
      this.mouseDownWindowPos
    )
    const mouseDownTime = this.timeDimenScale.invert(mouseDownCanvasPos.x)
    const timeStr = dayjs.unix(mouseDownTime).format('HH:mm')

    this.context.save()
    // draw time str
    this.context.textBaseline = 'top'
    this.context.fillText(
      timeStr,
      mouseDownCanvasPos.x + 4,
      this.timeXAxisHeight
    )
    this.context.restore()
  }

  private drawCurPosIndicator() {
    // not draw it when mouse move outside the canvas
    // to keep same as the chrome dev tool
    if (!this.insideCanvas || this.action !== Action.SelectingNewWindow) {
      return
    }

    const canvasPos = this.windowPosToCanvasPos(this.lastMouseWindowPos)
    const time = this.timeDimenScale.invert(canvasPos.x)
    const timeStr = dayjs.unix(time).format('HH:mm')

    this.context.save()
    // draw vertical line
    this.context.strokeStyle = CUR_POS_VERTICAL_LINE_STYLE
    this.context.lineWidth = CUR_POS_VERTICAL_LINE_WIDTH
    this.context.beginPath()
    this.context.moveTo(canvasPos.x, 0)
    this.context.lineTo(canvasPos.x, this.height)
    this.context.stroke()
    // draw time
    this.context.textBaseline = 'top'
    this.context.fillText(timeStr, canvasPos.x + 4, this.timeXAxisHeight)
    this.context.restore()
  }

  private drawSelectingNewWindow() {
    if (
      this.mouseDownWindowPos === null ||
      this.action !== Action.SelectingNewWindow
    ) {
      return
    }

    const mouseDownCanvasPos = this.windowPosToCanvasPos(
      this.mouseDownWindowPos
    )
    const deltaX = this.lastMouseWindowPos.x - this.mouseDownWindowPos.x

    this.context.save()
    // draw rect
    this.context.fillStyle = NEW_WINDOW_FILL_STYLE
    this.context.globalAlpha = NEW_WINDOW_ALPHA
    this.context.fillRect(mouseDownCanvasPos.x, 0, deltaX, this.height)
    this.context.restore()
  }

  private drawSelectedWindow() {
    if (!this.selectedTimeWindow) {
      return
    }

    const { start, end } = this.selectedTimeWindow
    const left = this.timeDimenScale(start)
    const right = this.timeDimenScale(end)

    const startTimeStr = dayjs.unix(start).format('HH:mm')
    const endTimeStr = dayjs.unix(end).format('HH:mm')

    this.context.save()
    // draw selected window area
    this.context.fillStyle = WINDOW_FILL_STYLE
    this.context.globalAlpha = WINDOW_ALPHA
    this.context.fillRect(left, 0, right - left, this.height)

    // draw window left and right borders
    this.context.strokeStyle = WINDOW_BORDER_STORKE_STYLE
    this.context.globalAlpha = WINDOW_BORDER_ALPHA
    this.context.lineWidth = WINDOW_BORDER_WIDTH
    // draw left border
    this.context.beginPath()
    this.context.moveTo(left, 0)
    this.context.lineTo(left, this.height)
    this.context.stroke()
    // draw right border
    this.context.beginPath()
    this.context.moveTo(right, 0)
    this.context.lineTo(right, this.height)
    this.context.stroke()

    // draw resize area
    this.context.strokeStyle = WINDOW_RESIZE_STROKE_STYLE
    this.context.lineWidth = WINDOW_RESIZE_LINE_WIDTH
    this.context.lineCap = 'round'
    // draw left
    this.context.beginPath()
    this.context.moveTo(left, this.dragAreaTop)
    this.context.lineTo(left, this.dragAreaBottom)
    this.context.stroke()
    // draw right
    this.context.beginPath()
    this.context.moveTo(right, this.dragAreaTop)
    this.context.lineTo(right, this.dragAreaBottom)
    this.context.stroke()

    // draw time
    this.context.fillStyle = '#0ca6f2'
    this.context.textBaseline = 'bottom'
    this.context.textAlign = 'right'
    this.context.fillText(startTimeStr, left - 2, this.dragAreaTop - 8)
    this.context.textAlign = 'left'
    this.context.fillText(endTimeStr, right + 2, this.dragAreaTop - 8)
    this.context.restore()
  }

  /////////////////////////////////////////////
  // utils
  private finalStartTime() {
    return this.oriStartTime + this.timeOffset
  }

  private finalEndTime() {
    return this.finalStartTime() + this.timeDuration
  }

  // trim start time to the begining of one day
  // for example: 2022-05-16 15:49 => 2022-05-16 00:00
  private trimStartTime() {
    return this.finalStartTime() - (this.finalStartTime() % ONE_DAY_SEC)
  }

  private calcXAxisTimeDelta() {
    // how seconds the 30px represnets
    const defTimeDelta =
      this.timeDimenScale.invert(TIME_POINT_MIN_WIDTH) -
      this.timeDimenScale.invert(0)
    const hour = Math.ceil(defTimeDelta / ONE_HOUR_SEC)
    return hour * ONE_HOUR_SEC
  }

  private windowPosToCanvasPos(windowPos: Pos) {
    const canvasBox = this.context.canvas.getBoundingClientRect()
    return {
      x: windowPos.x - canvasBox.left,
      y: windowPos.y - canvasBox.top
    }
  }

  //////////////////////////////////////////////
  // listeners
  private notifyTimeRangeListener(newTimeWindow: TimeRange) {
    this.onTimeWindowChange?.(newTimeWindow)
  }

  private notifyTimelineListener(newTimeline: TimeRange) {
    this.onTimelineChange?.(newTimeline)
  }
}

export default TimeWindowSelectController
